/**
 * voice-v8 — Eigener Anthropic-API-Schlüssel je Nutzer: die reinen Regeln. (12.10.2026, Bahn eigener-api-schluessel)
 *
 * Nur Form, Anzeige und Wörter — kein Speicher, kein Netz. Server und Seite lesen dieselben Zeilen.
 * ⚠️ Nichts hier gibt einen Schlüssel heraus: `endetAuf` zeigt vier Zeichen, mehr nie.
 */

/** Wie der Nutzer Vespera betreiben lässt: das Abo (nur Betreiber) oder der eigene Schlüssel. */
const API_MODI = ["abo", "api"] as const;
type ApiModus = (typeof API_MODI)[number];

export function istApiModus(x: unknown): x is ApiModus {
  return x === "abo" || x === "api";
}

/**
 * Was der BETREIBER je Konto im Adminbereich einstellt (Reiter „Zugang“): womit dieses Konto antwortet. Gespeichert in derselben Spalte wie
 * der Modus des Hauptkontos (ein Besitzer). „standard“ = nichts gespeichert, es gilt die Regel von heute.
 */
const ZUGANG_WAHL = ["abo", "api", "gemini"] as const;
export type ZugangWahl = (typeof ZUGANG_WAHL)[number];
export function istZugangWahl(x: unknown): x is ZugangWahl {
  return x === "abo" || x === "api" || x === "gemini";
}
export const ZUGANG_WAHL_TEXT: Readonly<Record<ZugangWahl | "standard", string>> = {
  standard: "Standard (wie heute)",
  abo: "Abo",
  api: "Eigener API-Schlüssel",
  gemini: "Gemini",
};

/** Ein Anthropic-Schlüssel: sk-ant-… aus Buchstaben, Ziffern, _ und -; 40–300 Zeichen. */
const FORM = /^sk-ant-[A-Za-z0-9_-]{30,290}$/;
const API_SCHLUESSEL_MIN = 40;
export const API_SCHLUESSEL_MAX = 300;

/** Ist das ein gültig GEFORMTER Schlüssel? (Ob er gilt, sagt erst der Test-Aufruf.) */
export function istApiSchluesselForm(x: unknown): x is string {
  return typeof x === "string" && x.length >= API_SCHLUESSEL_MIN && x.length <= API_SCHLUESSEL_MAX && FORM.test(x);
}

/** Die letzten vier Zeichen — das Einzige, was je angezeigt wird. */
export function endetAuf(schluessel: string): string {
  return schluessel.slice(-4);
}

/** „····abcd“ — die Anzeige. */
export function endetAufAnzeige(vier: string | null | undefined): string {
  return vier ? `····${vier}` : "";
}

/** Was ein Aufruf bei Anthropic ergab — feste Wörter, nie ein Fremdtext, nie der Schlüssel. */
export type PruefWort = "ok" | "ungueltig" | "kein-guthaben" | "gebremst" | "nicht-erreichbar" | "unklar";

export const PRUEF_SATZ: Readonly<Record<PruefWort, string>> = {
  ok: "Der Schlüssel gilt und es ist Guthaben da.",
  ungueltig: "Anthropic kennt diesen Schlüssel nicht. Bitte prüfe, ob du ihn ganz kopiert hast.",
  "kein-guthaben": "Der Schlüssel gilt, aber auf dem Konto ist kein Guthaben. Bitte lade es bei Anthropic auf.",
  gebremst: "Anthropic bremst gerade. Bitte versuch es in einer Minute noch einmal.",
  "nicht-erreichbar": "Anthropic ist gerade nicht erreichbar. Bitte versuch es gleich noch einmal.",
  unklar: "Das hat nicht geklappt. Der Schlüssel wurde nicht gespeichert.",
};

/** Sätze für die Sperre ohne Schlüssel und am Tageslimit — dieselben Wörter auf allen Wegen. */
export const SATZ_SCHLUESSEL_FEHLT = "Bitte hinterlege deinen API-Schlüssel in den Einstellungen unter „Modelle und Konto“.";
export const SATZ_TELEGRAM_OHNE_WEG = "Telegram läuft nur mit Abo oder mit eigenem API-Schlüssel. Schreib mir in der App oder stell den Zugang um.";
export const SATZ_VOM_BETREIBER = "Der Betreiber hat deinen Zugang festgelegt. Ändern kann er ihn im Adminbereich.";
export const SATZ_TAGESLIMIT = "Dein Tageslimit für den API-Zugang ist erreicht. Du kannst es in den Einstellungen unter „Modelle und Konto“ anheben; morgen geht es wieder.";
export const SATZ_SCHLUESSEL_STREIKT =
  "Dein API-Schlüssel wird von Anthropic nicht angenommen oder das Guthaben ist leer. Bitte prüfe ihn in den Einstellungen unter „Modelle und Konto“.";

/*
 * ── Gemini-Schlüssel je Konto (25.10.2026) ────────────────────────────────────────────────────────────────────────────
 * Bilder, Musik, Video-Analyse, Übersetzen und Belege laufen über Gemini. Bis heute rechnete dafür jedes Konto mit dem Schlüssel
 * des Betreibers; jetzt hinterlegt jedes Konto seinen eigenen (server/gemini-zugang.ts, geminiSchluesselFuer).
 */

/** Ein Google-AI-Studio-Schlüssel: AIza… aus Buchstaben, Ziffern, _ und -; 34–124 Zeichen. */
const GEMINI_FORM = /^AIza[A-Za-z0-9_-]{30,120}$/;
export const GEMINI_SCHLUESSEL_MAX = 124;

/** Ist das ein gültig GEFORMTER Gemini-Schlüssel? (Ob er gilt, sagt erst der Test-Aufruf.) */
export function istGeminiSchluesselForm(x: unknown): x is string {
  return typeof x === "string" && x.length <= GEMINI_SCHLUESSEL_MAX && GEMINI_FORM.test(x);
}

/** Was die Prüfung bei Google ergab — dieselben Wörter wie bei Anthropic, eigene Sätze. „kein-guthaben“ kennt Google hier nicht. */
export const GEMINI_PRUEF_SATZ: Readonly<Record<PruefWort, string>> = {
  ok: "Der Gemini-Schlüssel gilt.",
  ungueltig: "Google kennt diesen Schlüssel nicht. Bitte prüfe, ob du ihn ganz kopiert hast.",
  "kein-guthaben": "Der Schlüssel gilt, aber Google nimmt gerade keine Aufträge an. Bitte schau in deinem Google-Konto nach.",
  gebremst: "Google bremst gerade. Bitte versuch es in einer Minute noch einmal.",
  "nicht-erreichbar": "Google ist gerade nicht erreichbar. Bitte versuch es gleich noch einmal.",
  // Gilt für einen GESPEICHERTEN Schlüssel (erneut prüfen, Stand der Karte) — er bleibt, nur die Prüfung ging nicht.
  unklar: "Der Schlüssel ist gespeichert, konnte aber gerade nicht geprüft werden. Versuche es später noch einmal.",
};
/** Beim ersten Speichern ist „unklar“ ein Nein: ein ungeprüfter Schlüssel wird nicht abgelegt — das sagt dieser Satz. */
export const SATZ_GEMINI_NICHT_GESPEICHERT = "Das hat nicht geklappt. Der Schlüssel wurde nicht gespeichert. Versuche es später noch einmal.";

/** Die Meldung, wenn ein Konto Gemini braucht, aber keinen eigenen Schlüssel hat — auf allen Wegen dieselbe. */
export const SATZ_GEMINI_SCHLUESSEL_FEHLT = "Bitte hinterlege deinen Gemini-Schlüssel in den Einstellungen unter „Modelle und Konto“.";
/** Der Betreiber hat keinen eigenen und auf dem Server liegt keiner. */
export const SATZ_GEMINI_NICHT_EINGERICHTET = "Gemini ist auf diesem Server nicht eingerichtet.";

/** Vorgaben des Tageslimits in Cent. */
export const TAGESLIMIT_BETREIBER_CENT = 500;
export const TAGESLIMIT_FREMDE_CENT = 200;
export const TAGESLIMIT_MAX_CENT = 100_000;

/** Eingetragenes Guthaben: 0,01 bis 100.000 Euro. */
export const GUTHABEN_MIN_CENT = 1;
export const GUTHABEN_MAX_CENT = 10_000_000;

/** Euro (Zahl) → ganze Cent, oder null, wenn es keine erlaubte Zahl ist. */
export function guthabenCentAus(euro: unknown): number | null {
  if (typeof euro !== "number" || !Number.isFinite(euro)) return null;
  const cent = Math.round(euro * 100);
  return cent >= GUTHABEN_MIN_CENT && cent <= GUTHABEN_MAX_CENT ? cent : null;
}

/** Dollar → Euro für die Anzeige (grob, fester Kurs; keine Buchhaltung). */
export const USD_IN_EUR = 0.92;

/** Anzeige „ca. 1,23 €“. */
export function euroText(cent: number): string {
  const e = Math.max(0, cent) / 100;
  return `ca. ${e.toFixed(2).replace(".", ",")} €`;
}
